"use client";

import { BackendTable, useBackendTable, useBackendSort } from "./backend-table";
import { BackendSearchInput, useBackendSearch } from "./backend-search";
import { useDragDropTable } from "@/app/hooks/use-drag-drop-table";
import { DndContext } from "@dnd-kit/core";
import { useState, useEffect, useMemo, useCallback } from "react";
import { removeSortIndex } from "@/app/services/server/actions/remove-sort-index";
import { QuickActions } from "./quick-actions";
import {
  BackendDesktopFilterSheet,
  BackendFilterBar,
  BackendMobileFilterDrawer,
  excludeArchivedRowsUnlessSelected,
  useBackendFilters,
} from "./backend-filters";
import { cn } from "@/lib/utils";
import type {
  BackendListTablePropsT,
  QuickActionT,
} from "@/app/types/backend-table";

function StatusTab({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex shrink-0 items-center gap-1.5 rounded-[8px] px-3 py-1.5 text-[13px] font-semibold transition-colors",
        active
          ? "bg-[var(--c-ink)] text-white"
          : "text-[var(--c-muted)] hover:text-[var(--c-ink)]"
      )}
    >
      {label}
      <span
        className={cn(
          "rounded-full px-1.5 py-px font-[family-name:var(--font-backend-mono)] text-[10.5px]",
          active
            ? "bg-white/20 text-white"
            : "bg-[var(--c-border-soft-2)] text-[var(--c-muted)]"
        )}
      >
        {count}
      </span>
    </button>
  );
}

export function BackendListTable({
  data,
  columns,
  searchableFields,
  routeConfig,
  searchPlaceholder = "Suchen...",
  emptyMessage = "Keine Daten gefunden.",
  enableDragDrop = false,
  tableName,
  onReorderComplete,
  onSortIndexRemoved,
  quickActions,
  filters,
  singleTabLabel,
}: BackendListTablePropsT) {
  const [isClient, setIsClient] = useState(false);
  const [isRemovingSort, setIsRemovingSort] = useState(false);

  // Ensure we're on the client side to avoid hydration mismatch
  useEffect(() => {
    setIsClient(true);
  }, []);

  const {
    filteredData: filterFilteredData,
    filterValues,
    setFilterValue,
    clearAllFilters,
    activeFilterCount,
    filtersWithOptions,
  } = useBackendFilters(data, filters);

  // The status filter is rendered as tabs (left, above the table), the rest as
  // dropdowns next to the search (right).
  const statusFilterDef = useMemo(
    () => filters?.find((filter) => filter.id === "status") ?? null,
    [filters]
  );
  const otherFilters = useMemo(
    () => filtersWithOptions.filter((filter) => filter.id !== "status"),
    [filtersWithOptions]
  );
  const activeStatus = useMemo(() => {
    const value = filterValues.status;
    return Array.isArray(value) && value.length > 0 ? value[0] : null;
  }, [filterValues.status]);
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { __all: 0 };
    for (const row of data) {
      const status = String(row.status ?? "");
      counts[status] = (counts[status] ?? 0) + 1;
      if (status !== "archived") {
        counts.__all += 1;
      }
    }
    return counts;
  }, [data]);
  const shouldRenderSingleTab = Boolean(!statusFilterDef && singleTabLabel);

  // Filter out archived items by default, unless status filter includes "archived"
  const dataWithArchivedFilter = useMemo(
    () =>
      excludeArchivedRowsUnlessSelected(
        filterFilteredData,
        filterValues.status
      ),
    [filterFilteredData, filterValues.status]
  );

  // Use search hook (after filters)
  const { filteredData, searchTerm, setSearchTerm } = useBackendSearch(
    dataWithArchivedFilter,
    searchableFields
  );

  // Always call hooks (conditional logic moved inside)
  const dragDropHook = useDragDropTable(
    filteredData,
    tableName || "",
    onReorderComplete
  );

  const baseData =
    enableDragDrop && tableName && isClient ? dragDropHook.items : filteredData;

  const sortHook = useBackendSort(baseData);
  const dragEnabled =
    enableDragDrop &&
    Boolean(tableName) &&
    isClient &&
    !sortHook.sortColumn &&
    !sortHook.sortDirection;

  // Determine which data to use (only enable drag & drop on client)
  const displayData = sortHook.sortedData;

  const {
    selectedRows,
    currentPage,
    itemsPerPage,
    handleRowSelect,
    handleSelectAll,
    handlePageChange,
    handleItemsPerPageChange,
  } = useBackendTable(displayData);

  const handleRemoveSortIndex = useCallback(
    async (selectedIds?: string[]) => {
      const idsToUse = selectedIds || selectedRows;
      if (!tableName || idsToUse.length === 0) {
        return;
      }

      setIsRemovingSort(true);
      try {
        await removeSortIndex(tableName, idsToUse);
        handleSelectAll(false);
        onSortIndexRemoved?.();
        onReorderComplete?.();
      } catch (error) {
        console.error("Fehler beim Entfernen der Sortierung:", error);
      } finally {
        setIsRemovingSort(false);
      }
    },
    [
      tableName,
      selectedRows,
      handleSelectAll,
      onSortIndexRemoved,
      onReorderComplete,
    ]
  );

  // Build quick actions array with selection clearing wrapper
  const allQuickActions = useMemo<QuickActionT[]>(() => {
    const actions: QuickActionT[] = [];

    // Add "Sortierung entfernen" action if tableName is provided
    if (tableName) {
      actions.push({
        id: "remove-sort-index",
        label: "Sortierung entfernen",
        loadingLabel: "Entfernen...",
        onClick: async (selectedIds: string[]) => {
          await handleRemoveSortIndex(selectedIds);
        },
        disabled: isRemovingSort,
        loading: isRemovingSort,
      });
    }

    // Add custom quick actions with selection clearing wrapper
    if (quickActions) {
      actions.push(
        ...quickActions.map((action) => ({
          ...action,
          onClick: async (selectedIds: string[]) => {
            await action.onClick(selectedIds);
            // Clear selection after action completes (for archive, duplicate, etc.)
            // This ensures Quick Actions disappear when items are no longer selected
            handleSelectAll(false);
          },
        }))
      );
    }

    return actions;
  }, [
    tableName,
    isRemovingSort,
    quickActions,
    handleRemoveSortIndex,
    handleSelectAll,
  ]);

  const tableContent = (
    <BackendTable
      columns={columns}
      data={displayData}
      selectedRows={selectedRows}
      onRowSelect={handleRowSelect}
      onSelectAll={handleSelectAll}
      onSort={sortHook.handleSort}
      sortColumn={sortHook.sortColumn}
      sortDirection={sortHook.sortDirection}
      currentPage={currentPage}
      totalItems={displayData.length}
      itemsPerPage={itemsPerPage}
      onItemsPerPageChange={handleItemsPerPageChange}
      onPageChange={handlePageChange}
      routeConfig={routeConfig}
      enableDragDrop={dragEnabled}
      emptyMessage={emptyMessage}
    />
  );

  return (
    <>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        {(statusFilterDef || shouldRenderSingleTab) && (
          <div className="scrollbar-hide scrollbar-hide-webkit hidden max-w-full items-center gap-1 overflow-x-auto rounded-[12px] border border-[var(--c-border-card)] bg-[var(--c-card)] p-[5px] md:flex">
            {statusFilterDef ? (
              <>
                <StatusTab
                  label="Alle"
                  count={statusCounts.__all ?? 0}
                  active={!activeStatus}
                  onClick={() => setFilterValue("status", [])}
                />
                {statusFilterDef.options.map((option) => (
                  <StatusTab
                    key={option.value}
                    label={option.label}
                    count={statusCounts[option.value] ?? 0}
                    active={activeStatus === option.value}
                    onClick={() => setFilterValue("status", [option.value])}
                  />
                ))}
              </>
            ) : (
              <StatusTab
                label={singleTabLabel ?? ""}
                count={data.length}
                active
                onClick={() => undefined}
              />
            )}
          </div>
        )}

        <div className="scrollbar-hide scrollbar-hide-webkit ml-auto flex min-w-0 flex-1 items-center gap-3 overflow-x-auto max-[860px]:ml-0 max-[860px]:basis-full md:flex-none max-[860px]:md:flex-[1_1_100%]">
          <div className="min-w-0 flex-1 md:w-[220px] md:flex-none max-[860px]:md:w-auto max-[860px]:md:flex-[1_1_0]">
            <BackendSearchInput
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              placeholder={searchPlaceholder}
            />
          </div>

          {filtersWithOptions.length > 0 && (
            <div className="flex-none md:hidden">
              <BackendMobileFilterDrawer
                filters={filtersWithOptions}
                values={filterValues}
                onChange={setFilterValue}
                onClearAll={clearAllFilters}
                activeFilterCount={activeFilterCount}
              />
            </div>
          )}

          {otherFilters.length > 0 && (
            <div className="hidden flex-none md:block 2xl:hidden">
              <BackendDesktopFilterSheet
                filters={otherFilters}
                values={filterValues}
                onChange={setFilterValue}
                onClearAll={clearAllFilters}
                activeFilterCount={activeFilterCount}
              />
            </div>
          )}

          {otherFilters.length > 0 && (
            <BackendFilterBar
              className="hidden 2xl:flex"
              filters={otherFilters}
              values={filterValues}
              onChange={setFilterValue}
              onClearAll={clearAllFilters}
              activeFilterCount={activeFilterCount}
            />
          )}
        </div>
      </div>

      <QuickActions
        actions={allQuickActions}
        selectedCount={selectedRows.length}
        selectedIds={selectedRows}
        onClear={() => handleSelectAll(false)}
      />

      {/* Backend Table */}
      <div className="mt-6">
        {dragEnabled ? (
          <DndContext
            sensors={dragDropHook.sensors}
            collisionDetection={dragDropHook.closestCenter}
            onDragEnd={dragDropHook.handleDragEnd}
          >
            {tableContent}
          </DndContext>
        ) : (
          tableContent
        )}
      </div>
    </>
  );
}
